































  import Vue from 'vue';
  import { Component, Prop } from 'vue-property-decorator';
  import { toKebabCase } from '../utils/string';
  import { ListItem, VueCSSClasses } from '../utils/types';
  import { XEmit } from './decorators/bus.decorators';
  import { XInject } from './decorators/injection.decorators';
  import { LIST_ITEMS_KEY } from './decorators/injection.consts';

  /**
   * The type returned by the gridItems function. Basically it's a list of items with its CSS
   * classes and a slotName.
   *
   * @internal
   */
  interface GridItem {
    slotName: string;
    item: ListItem;
    cssClass: VueCSSClasses;
  }

  /**
   * Grid component that is able to render different items based on their modelName value. In order
   * to achieve this, it exposes a scopedSlot for each different modelName. In case the items used
   * do not have modelName property, the default slot is used instead. It has a required property:
   * the `items` to render; and an optional one: the number `columns` the grid is divided in. If the
   * number of columns is not specified, the grid automatically fills the rows with as many columns
   * as it can fit.
   *
   * @public
   */
  @Component({})
  export default class BaseGrid extends Vue {
    /**
     * Animation component that will be used to animate the base grid.
     *
     * @public
     */
    @Prop({ default: 'ul' })
    protected animation!: Vue | string;

    /**
     * Number of columns the grid is divided into. By default, its value is 0, setting the grid
     * columns mode to auto-fill.
     *
     * @public
     */
    @Prop({ default: 0 })
    protected columns!: number;

    /**
     * The list of items to be rendered.
     *
     * @remarks The items must have an id property.
     *
     * @public
     */
    @Prop()
    protected items!: ListItem[];

    /**
     * It injects {@link ListItem} provided by an ancestor.
     *
     * @internal
     */
    @XInject(LIST_ITEMS_KEY)
    public injectedListItems!: ListItem[];

    /**
     * Emits the {@link XEventsTypes.RenderedColumnsNumberChanged}
     * event whenever the number of columns rendered inside the grid changes.
     *
     * @internal
     */
    @XEmit('RenderedColumnsNumberChanged', { immediate: false })
    public renderedColumnsNumber = 0;

    /**
     * It returns the items passed as props or the injected ones.
     *
     * @returns List of grid items.
     *
     * @public
     */
    protected get computedItems(): ListItem[] {
      return (
        this.items ??
        this.injectedListItems ??
        //TODO: add here logger
        //eslint-disable-next-line no-console
        console.warn('It is necessary to pass a prop or inject the list of filters')
      );
    }

    /**
     * CSS class based on the column property value so items inside the grid can fill different
     * amount of columns or rows based on how many columns the grid is divided into.
     *
     * @returns CSS class with the column property value.
     *
     * @internal
     */
    protected get cssClasses(): VueCSSClasses {
      return this.columns ? `x-base-grid--cols-${this.columns}` : 'x-base-grid--cols-auto';
    }

    /**
     * CSSStyleDeclaration object specifying the number of columns the grid is divided into based on
     * the column property value.
     *
     * @returns A CSSStyleDeclaration to use as the style attribute.
     *
     * @internal
     */
    protected get style(): Partial<CSSStyleDeclaration> {
      return {
        gridTemplateColumns: this.columns
          ? `repeat(${this.columns}, minmax(0, 1fr))`
          : 'repeat(auto-fill, minmax(var(--x-size-min-width-grid-item, 150px), 1fr))'
      };
    }

    /**
     * Maps the item to an object containing: the `item`, its `CSS class` and its slot name.
     *
     * @returns An array of objects containing the item and its CSS class.
     *
     * @internal
     */
    protected get gridItems(): GridItem[] {
      return this.computedItems.map(item => {
        const slotName = toKebabCase(item.modelName);
        return {
          slotName,
          item,
          cssClass: `x-base-grid__${slotName}`
        };
      });
    }

    /**
     * Initialises the rendered columns number and sets a ResizeObserver to keep it updated.
     *
     * @internal
     */
    protected mounted(): void {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      const resizeObserver = new ResizeObserver(this.updateRenderedColumnsNumber);
      resizeObserver.observe(this.$el);
      this.$on('hook:beforeDestroy', () => {
        resizeObserver.disconnect();
      });
    }

    /**
     * Updates the number of columns rendered inside the grid.
     *
     * @internal
     */
    protected updateRenderedColumnsNumber(): void {
      const { gridTemplateColumns } = getComputedStyle(this.$el);
      this.renderedColumnsNumber = gridTemplateColumns.split(' ').length;
    }
  }
